import { Skeleton } from "@credit-acceptance/uikit";
import { useTranslation } from "react-i18next";

import { EmptyState, ErrorState } from "../../components/ui/centeredMessage";
import { getErrorMessage } from "../../lib/query/errors";
import type { StatusHistoryEntry } from "./api/statusHistoryTypes";
import { useStatusHistory } from "./api/useStatusHistory";
import AuditTable, { type AuditTableColumn } from "./AuditTable";
import { formatStatusHistoryDate } from "./formatStatusHistoryDate";

interface StatusHistoryTabProperties {
  approvalId: string;
}

function StatusHistoryTab({ approvalId }: StatusHistoryTabProperties) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useStatusHistory(approvalId);

  const rows = data ?? [];

  const columns: Array<AuditTableColumn<StatusHistoryEntry>> = [
    { key: "workqueue", header: t("audit.statusHistory.columns.workqueue"), render: (row) => row.workqueue },
    {
      key: "startStatus",
      header: t("audit.statusHistory.columns.startStatus"),
      render: (row) => (
        <div className="whitespace-nowrap">
          {row.status}
          {row.startDate && <div className="text-xs">{formatStatusHistoryDate(row.startDate)}</div>}
        </div>
      ),
    },
    { key: "startPhase", header: t("audit.statusHistory.columns.startPhase"), render: (row) => row.phase },
    { key: "user", header: t("audit.statusHistory.columns.user"), render: (row) => row.user },
    {
      key: "endStatus",
      header: t("audit.statusHistory.columns.endStatus"),
      render: (row) => (
        <div className="whitespace-nowrap">
          {row.endStatus}
          {row.endDate && <div className="text-xs">{formatStatusHistoryDate(row.endDate)}</div>}
        </div>
      ),
    },
    { key: "endPhase", header: t("audit.statusHistory.columns.endPhase"), render: (row) => row.endPhase },
  ];

  return (
    <div className="flex h-full flex-col">
      {isLoading && (
        <div role="status" aria-label={t("audit.statusHistory.loading")} className="flex-1 p-3">
          <Skeleton loading active paragraph={{ rows: 6 }} />
        </div>
      )}

      {isError && (
        <ErrorState message={t("audit.statusHistory.loadError", { message: getErrorMessage(error) })} className="p-0" />
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <EmptyState message={t("audit.statusHistory.empty")} className="p-0" />
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <AuditTable caption={t("audit.statusHistory.caption")} columns={columns} rows={rows} stickyHeader />
      )}
    </div>
  );
}

export default StatusHistoryTab;
